import type {
  AnyOfRule,
  CompareRule,
  FieldValidation,
  RequireWhenRule,
} from "@/generated";

// The generated union is inlined on InboxResponse.validation_rules rather
// than exported; reconstruct it from the parts.
export type CrossFieldRule = CompareRule | RequireWhenRule | AnyOfRule;
import type { FieldDef, TableColumn } from "./types";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^\+?[0-9(][0-9\s().-]{5,}$/;

const TEXTUAL_TYPES = new Set(["text", "textarea", "email", "phone", "url"]);

export const isEmpty = (value: unknown): boolean =>
  value == null || (typeof value === "string" && value.trim() === "");

const normalize = (
  value: string,
  rules: FieldValidation | null | undefined
): string => {
  if (!rules) return value;
  let out = value;
  if (rules.trim) out = out.trim();
  if (rules.text_case === "upper") out = out.toUpperCase();
  else if (rules.text_case === "lower") out = out.toLowerCase();
  return out;
};

const ibanValid = (raw: string): boolean => {
  const compact = raw.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(compact)) return false;
  const rearranged = compact.slice(4) + compact.slice(0, 4);
  const digits = rearranged.replace(/[A-Z]/g, (c) =>
    String(c.charCodeAt(0) - 55)
  );
  let remainder = 0;
  for (const ch of digits) {
    remainder = (remainder * 10 + Number(ch)) % 97;
  }
  return remainder === 1;
};

const luhnValid = (raw: string): boolean => {
  const digits = raw.replace(/[\s-]/g, "");
  if (!/^[0-9]{8,}$/.test(digits)) return false;
  let total = 0;
  const reversed = digits.split("").reverse();
  for (let i = 0; i < reversed.length; i++) {
    let d = Number(reversed[i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return total % 10 === 0;
};

const eanValid = (raw: string): boolean => {
  const digits = raw.replace(/\s/g, "");
  if (!/^[0-9]+$/.test(digits) || ![8, 12, 13, 14].includes(digits.length))
    return false;
  const body = digits.slice(0, -1);
  const check = Number(digits[digits.length - 1]);
  let total = 0;
  for (let i = 0; i < body.length; i++) {
    const weight = i % 2 === (body.length - 1) % 2 ? 3 : 1;
    total += Number(body[i]) * weight;
  }
  return (10 - (total % 10)) % 10 === check;
};

const nifPtValid = (raw: string): boolean => {
  const digits = raw.replace(/\s/g, "");
  if (!/^[0-9]{9}$/.test(digits)) return false;
  let total = 0;
  for (let i = 0; i < 8; i++) total += Number(digits[i]) * (9 - i);
  let check = 11 - (total % 11);
  if (check >= 10) check = 0;
  return check === Number(digits[8]);
};

const NAMED_FORMATS: Record<string, [(v: string) => boolean, string]> = {
  iban: [ibanValid, "is not a valid IBAN"],
  luhn: [luhnValid, "does not pass the card-number check"],
  ean: [eanValid, "is not a valid EAN/GTIN barcode"],
  nif_pt: [nifPtValid, "is not a valid Portuguese NIF"],
};

const isoDaysFromToday = (offset: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

/** User-facing messages start with a capital; raw fragments stay lowercase
 * so composed contexts ("Row 1 · Qty: must be…") read naturally. */
export const sentenceCase = (message: string): string =>
  message.charAt(0).toUpperCase() + message.slice(1);

const formatError = (value: string, type: string): string | null => {
  if (type === "email" && !EMAIL_RE.test(value))
    return "is not a valid email address";
  if (type === "url") {
    try {
      const parsed = new URL(value);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:")
        return "is not a valid URL (http/https)";
    } catch {
      return "is not a valid URL (http/https)";
    }
  }
  if (type === "phone" && !PHONE_RE.test(value))
    return "is not a valid phone number";
  return null;
};

/**
 * Client mirror of the backend's `_check_value` — UX only, the server is
 * authoritative. Returns the first violated rule's message, or null.
 */
export function validateValue(
  value: unknown,
  type: string,
  rules: FieldValidation | null | undefined
): string | null {
  if (!rules || isEmpty(value)) return null;

  if (typeof value === "string") {
    // Mirror the server: normalization happens before any check, so both
    // sides judge the canonical value.
    value = normalize(value, rules);
  }

  if (TEXTUAL_TYPES.has(type) && typeof value === "string") {
    if (rules.min_length != null && value.length < rules.min_length)
      return `must be at least ${rules.min_length} characters`;
    if (rules.max_length != null && value.length > rules.max_length)
      return `must be at most ${rules.max_length} characters`;
    if (rules.pattern != null) {
      let matches = true;
      try {
        matches = new RegExp(`^(?:${rules.pattern})$`).test(value);
      } catch {
        matches = true;
      }
      if (!matches)
        return (
          rules.pattern_message || `must match the pattern ${rules.pattern}`
        );
    }
    if (rules.check_format) {
      const formatReason = formatError(value, type);
      if (formatReason) return formatReason;
    }
    if (rules.format && NAMED_FORMATS[rules.format]) {
      const [checker, message] = NAMED_FORMATS[rules.format];
      if (!checker(value)) return message;
    }
  }

  if (type === "number" && typeof value === "number") {
    if (rules.min_value != null && value < rules.min_value)
      return `must be at least ${rules.min_value}`;
    if (rules.max_value != null && value > rules.max_value)
      return `must be at most ${rules.max_value}`;
    if (rules.integer_only && !Number.isInteger(value))
      return "must be a whole number";
    if (
      rules.max_decimals != null &&
      Number(value.toFixed(rules.max_decimals)) !== value
    )
      return `must have at most ${rules.max_decimals} decimal places`;
  }

  if (type === "file" && typeof value === "object" && value !== null) {
    const file = value as { name?: unknown; size?: unknown };
    if (
      rules.allowed_extensions?.length &&
      typeof file.name === "string" &&
      file.name.includes(".")
    ) {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!rules.allowed_extensions.includes(ext))
        return `must be one of: ${rules.allowed_extensions.join(", ")}`;
    }
    if (
      rules.max_file_size_mb != null &&
      typeof file.size === "number" &&
      file.size > rules.max_file_size_mb * 1024 * 1024
    )
      return `must be at most ${rules.max_file_size_mb} MB`;
  }

  if (type === "date" && typeof value === "string") {
    const day = value.slice(0, 10);
    let minBound = rules.min_date ?? null;
    if (rules.min_date_offset_days != null) {
      const relative = isoDaysFromToday(rules.min_date_offset_days);
      if (minBound == null || relative > minBound) minBound = relative;
    }
    let maxBound = rules.max_date ?? null;
    if (rules.max_date_offset_days != null) {
      const relative = isoDaysFromToday(rules.max_date_offset_days);
      if (maxBound == null || relative < maxBound) maxBound = relative;
    }
    if (minBound != null && day < minBound)
      return `must be on or after ${minBound}`;
    if (maxBound != null && day > maxBound)
      return `must be on or before ${maxBound}`;
  }

  return null;
}

export type FieldViolation = {
  message: string;
  strict: boolean;
  // Table cell violations render in the cell itself (ring + corner marker +
  // tooltip); consumers can skip the field-level message for them.
  inCell?: boolean;
};

/**
 * Validate a full field value (scalar or table rows) against its rules.
 * Table fields check each cell against its column's rules.
 */
export function validateField(
  field: Pick<FieldDef, "type" | "options"> & {
    validation?: FieldValidation | null;
  },
  value: unknown
): FieldViolation | null {
  if (field.type === "table") {
    if (!Array.isArray(value)) return null;
    const fieldRules = field.validation;
    if (fieldRules?.min_rows != null && value.length < fieldRules.min_rows) {
      return {
        message: sentenceCase(`must have at least ${fieldRules.min_rows} rows`),
        strict: Boolean(fieldRules.strict),
      };
    }
    if (fieldRules?.max_rows != null && value.length > fieldRules.max_rows) {
      return {
        message: sentenceCase(`must have at most ${fieldRules.max_rows} rows`),
        strict: Boolean(fieldRules.strict),
      };
    }
    if (!Array.isArray(field.options)) return null;
    const columns = field.options.filter(
      (o): o is TableColumn => typeof o === "object" && o !== null
    );
    const seenByColumn = new Map<string, Set<unknown>>();
    for (let i = 0; i < value.length; i++) {
      const row = value[i];
      if (typeof row !== "object" || row === null) continue;
      for (const col of columns) {
        if (!col.validation) continue;
        const cell = (row as Record<string, unknown>)[col.name];
        const message = validateValue(cell, col.type, col.validation);
        if (message) {
          return {
            message: `Row ${i + 1} · ${col.label || col.name}: ${message}`,
            strict: Boolean(col.validation.strict),
            inCell: true,
          };
        }
        if (col.validation.unique_in_table && !isEmpty(cell)) {
          const seen = seenByColumn.get(col.name) ?? new Set<unknown>();
          if (seen.has(cell)) {
            return {
              message: `Row ${i + 1} · ${col.label || col.name}: duplicate value '${String(cell)}' in this table`,
              strict: Boolean(col.validation.strict),
              inCell: true,
            };
          }
          seen.add(cell);
          seenByColumn.set(col.name, seen);
        }
      }
    }
    return null;
  }

  const message = validateValue(value, field.type, field.validation);
  return message
    ? {
        message: sentenceCase(message),
        strict: Boolean(field.validation?.strict),
      }
    : null;
}

type RuleField = { id: string; name: string; type: string };

const OP_FNS: Record<
  string,
  (a: string | number, b: string | number) => boolean
> = {
  lt: (a, b) => a < b,
  lte: (a, b) => a <= b,
  gt: (a, b) => a > b,
  gte: (a, b) => a >= b,
  eq: (a, b) => a === b,
  neq: (a, b) => a !== b,
};

const comparable = (value: unknown, type: string): string | number | null => {
  if (isEmpty(value)) return null;
  if (type === "number") return typeof value === "number" ? value : null;
  if (type === "date")
    return typeof value === "string" ? value.slice(0, 10) : null;
  return String(value);
};

const conditionHolds = (
  when: RequireWhenRule["when"],
  value: unknown
): boolean => {
  if (when.op === "empty") return isEmpty(value);
  if (when.op === "not_empty") return !isEmpty(value);
  if (isEmpty(value)) return false;
  const text = String(value);
  if (when.op === "eq") return text === String(when.value);
  if (when.op === "neq") return text !== String(when.value);
  const expected = Array.isArray(when.value) ? when.value : [when.value];
  return expected.map(String).includes(text);
};

/**
 * Client mirror of the server's cross-field evaluation over the saved
 * record. Returns violations keyed by field id so every participant gets
 * a ring and message.
 */
export function evaluateCrossFieldRules(
  fields: RuleField[],
  rules: CrossFieldRule[] | null | undefined,
  record: Record<string, unknown>
): Map<string, FieldViolation> {
  const out = new Map<string, FieldViolation>();
  if (!rules?.length) return out;
  const byId = new Map(fields.map((f) => [String(f.id), f]));
  const emit = (ids: string[], message: string, strict: boolean) => {
    for (const id of ids) {
      if (byId.has(id) && !out.has(id))
        out.set(id, { message: sentenceCase(message), strict });
    }
  };
  for (const rule of rules) {
    if (rule.kind === "compare") {
      const compareRule = rule as CompareRule;
      const leftField = byId.get(compareRule.left);
      const rightField = byId.get(compareRule.right);
      if (!leftField || !rightField) continue;
      const left = comparable(record[compareRule.left], leftField.type);
      const right = comparable(record[compareRule.right], rightField.type);
      if (left == null || right == null) continue;
      if (!OP_FNS[compareRule.op]?.(left, right)) {
        const isDate = leftField.type === "date";
        const labels: Record<string, string> = {
          lt: isDate ? "before" : "less than",
          lte: isDate ? "on or before" : "at most",
          gt: isDate ? "after" : "greater than",
          gte: isDate ? "on or after" : "at least",
          eq: "equal to",
          neq: "different from",
        };
        emit(
          [compareRule.left, compareRule.right],
          compareRule.message ||
            `${leftField.name} must be ${labels[compareRule.op]} ${rightField.name}`,
          Boolean(compareRule.strict)
        );
      }
    } else if (rule.kind === "require_when") {
      const requireRule = rule as RequireWhenRule;
      const conditionField = byId.get(requireRule.when.field);
      if (!conditionField) continue;
      if (!conditionHolds(requireRule.when, record[requireRule.when.field]))
        continue;
      for (const target of requireRule.require ?? []) {
        if (byId.has(target) && isEmpty(record[target])) {
          emit(
            [target],
            requireRule.message ||
              `required when ${conditionField.name} is '${String(record[requireRule.when.field])}'`,
            Boolean(requireRule.strict)
          );
        }
      }
    } else {
      const anyRule = rule as AnyOfRule;
      const anyFields = anyRule.fields ?? [];
      if (!anyFields.length) continue;
      const minFilled = anyRule.min_filled ?? 1;
      const filled = anyFields.filter((t) => !isEmpty(record[t])).length;
      if (filled < minFilled) {
        const names = anyFields
          .map((t) => byId.get(t)?.name)
          .filter(Boolean)
          .join(", ");
        emit(
          anyFields,
          anyRule.message || `at least ${minFilled} of ${names} must be filled`,
          Boolean(anyRule.strict)
        );
      }
    }
  }
  return out;
}
